"use client";

import { useEffect, useRef, useState, type FC, type Ref } from "react";
import {
  type DynamicInputValidationResult,
  type DataFieldValue,
  type Timeout,
  ToggleInput,
  type ToggleInputProps,
  usePubSub,
} from "@huub-web/ui";

import { isArray } from "@huub-web/utils";

import { useTemplates } from "../../hooks/useTemplates";
import type { InputFieldProps } from "../../types";

export interface ToggleInputTemplateProps extends InputFieldProps {}

/**
 * A template for rendering a ToggleInput component.
 */
export const ToggleInputTemplate: FC<
  ToggleInputTemplateProps & { ref?: Ref<HTMLDivElement> }
> = ({ id, endpoint, linkedFields, ref, ...props }) => {
  const { updateDataFieldValue } = useTemplates();
  const pubSub = usePubSub();

  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  const [value, setValue] = useState<DataFieldValue>(props.value);
  const [initialValue, setInitialValue] = useState<DataFieldValue>(props.value);

  const validationRef = useRef<DynamicInputValidationResult | null>(null);

  // Change & stop editing handler
  // ------------------------------

  const handleChange: ToggleInputProps["onChange"] = (value, validation) => {
    validationRef.current = validation;
    setValue(value);
  };

  const handleStopEditing = async () => {
    setError(null);
    setSuccess(false);

    if (
      validationRef.current &&
      value?.toString().trim() !== initialValue?.toString().trim()
    ) {
      // console.log(
      //   "[ToggleInputTemplate] handleStopEditing",
      //   value,
      //   validationRef.current,
      // );

      if (validationRef.current.success) {
        setInitialValue(value);

        if (endpoint) {
          const response = await updateDataFieldValue(
            endpoint,
            value,
            linkedFields,
          );

          if (response.error) {
            setError(response.error);
            setSuccess(false);
          } else {
            setSuccess(true);
          }
        }
      } else {
        //
      }
    }
  };

  // Highlight
  // ------------------------------

  const [isHighlighted, setIsHighlighted] = useState(false);

  useEffect(() => {
    let timeout: Timeout;

    const unsubscribeHighlight = pubSub.subscribe(
      "inputHighlight",
      ({ ids }) => {
        if (id && ids.includes(id)) {
          setIsHighlighted(true);

          timeout = setTimeout(() => {
            setIsHighlighted(false);
          }, 5000);
        }
      },
    );

    const unsubscribeUpdate = pubSub.subscribe("inputUpdate", (payload) => {
      if (!payload) {
        return;
      }
      const updates = isArray(payload) ? payload : [payload];

      for (const update of updates) {
        if (!update) continue;
        const { id: updateID, value } = update;
        if (id === updateID) {
          setValue?.(value);
          break;
        }
      }
    });

    return () => {
      unsubscribeHighlight();
      unsubscribeUpdate();
      clearTimeout(timeout);
    };
  }, [id]);

  // Successfull update
  // ------------------------------

  const successTimeOutRef = useRef<Timeout | null>(null);
  useEffect(() => {
    if (success) {
      successTimeOutRef.current = setTimeout(() => {
        setSuccess(false);
      }, 3000);
    } else if (successTimeOutRef.current) {
      clearTimeout(successTimeOutRef.current);
    }

    return () => {
      if (successTimeOutRef.current) {
        clearTimeout(successTimeOutRef.current);
      }
    };
  }, [success]);

  return (
    <ToggleInput
      ref={ref}
      id={id}
      {...props}
      success={success}
      highlight={isHighlighted}
      error={error ?? undefined}
      value={value === null ? undefined : value}
      onChange={handleChange}
      onStopEditing={handleStopEditing}
    />
  );
};
